import type { CsfCheck } from "./csf"
import { ANSWER_KEYS, emptyIntake, inferTouched, type AnswerKey, type Intake } from "./intake"

export const WORKSPACE_VERSION = 1

export interface CheckState {
  done: boolean
  doneAt?: string
  owner?: string
  /** True when the person decided not to do it (for example not wiping a device), with the reason in `note`. */
  declined?: boolean
  note?: string
}

/** A task handed to someone (usually a contact), tracked so the Timeline shows who did what and when. */
export interface Assignment {
  id: string
  title: string
  contactId: string | null
  assigneeName: string
  status: "open" | "done" | "cancelled"
  createdAt: string
  dueAt?: string
  doneAt?: string
  note: string
  /** The checklist item this task came from, if any. Finishing either one finishes the other. */
  itemId?: string
}

export interface LogEntry {
  id: string
  at: string // ISO
  text: string
}

export interface Contact {
  id: string
  role: string // e.g. "Cyber insurer", "Counsel", "IR firm", "FBI field office"
  name: string
  phone: string
  notes: string
}

export interface EvidenceItem {
  id: string
  what: string
  from: string
  collectedBy: string
  collectedAt: string
  location: string
}

/** User adjustments to a computed deadline clock, e.g. after talking to counsel. */
export interface ClockOverride {
  /** Counsel-confirmed start of the legal clock (ISO). Recomputes the due date from the rule's duration. */
  startIso?: string
  /** Counsel-confirmed due date/time (ISO). Wins over everything else. */
  dueIso?: string
  /** Counsel says this does not apply to us. Hidden from the clocks column (still listed under 'hidden'). */
  hidden?: boolean
  note?: string
  /**
   * Periods when the user paused this clock (for example a law-enforcement delay or counsel's
   * instruction). While paused the due date slides forward so the remaining time is frozen.
   * Pausing is the user's decision; most legal deadlines do not stop on their own.
   */
  pauses?: PauseSpan[]
}

export interface PauseSpan { fromIso: string; toIso?: string; reason?: string }

/** A clock the user adds themselves (contract notice window, insurer deadline, counsel's date...). */
export interface CustomClock {
  id: string
  label: string
  /** ISO due date/time. */
  dueIso: string
  note: string
}

export interface Workspace {
  version: number
  id: string
  createdAt: string
  updatedAt: string
  /** Optional label (provider mode will use this as the client name). */
  label: string
  intake: Intake
  intakeComplete: boolean
  /** True once the user has read the two explainer pages. */
  introSeen: boolean
  clockOverrides: Record<string, ClockOverride>
  customClocks: CustomClock[]
  assignments: Assignment[]
  /** Answers to the after-the-incident NIST CSF self-check (one per function). */
  csfCheck: CsfCheck
  checks: Record<string, CheckState>
  log: LogEntry[]
  contacts: Contact[]
  evidence: EvidenceItem[]
}

export function uid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID()
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

export function newWorkspace(now = new Date()): Workspace {
  const t = now.toISOString()
  return {
    version: WORKSPACE_VERSION,
    id: uid(),
    createdAt: t,
    updatedAt: t,
    label: "",
    intake: emptyIntake(),
    intakeComplete: false,
    introSeen: false,
    clockOverrides: {},
    customClocks: [],
    assignments: [],
    csfCheck: {},
    checks: {},
    log: [],
    contacts: [],
    evidence: [],
  }
}

/** Validate/repair data coming from storage or an imported file. Returns null if unusable. */
export function parseWorkspace(raw: unknown): Workspace | null {
  if (!raw || typeof raw !== "object") return null
  const w = raw as Partial<Workspace>
  if (typeof w.id !== "string" || !w.id) return null
  const base = newWorkspace()
  const merged = { ...emptyIntake(), ...(w.intake ?? {}) } as Intake
  const touched: AnswerKey[] = Array.isArray((w.intake as Partial<Intake> | undefined)?.touched)
    ? ((w.intake as Intake).touched.filter((k) => (ANSWER_KEYS as readonly string[]).includes(k)))
    : inferTouched(merged)
  return {
    ...base,
    ...w,
    version: WORKSPACE_VERSION,
    intake: { ...merged, touched },
    checks: w.checks && typeof w.checks === "object" ? w.checks : {},
    log: Array.isArray(w.log) ? w.log : [],
    contacts: Array.isArray(w.contacts) ? w.contacts : [],
    evidence: Array.isArray(w.evidence) ? w.evidence : [],
    label: typeof w.label === "string" ? w.label : "",
    intakeComplete: !!w.intakeComplete,
    introSeen: !!w.introSeen,
    clockOverrides: w.clockOverrides && typeof w.clockOverrides === "object" && !Array.isArray(w.clockOverrides) ? w.clockOverrides : {},
    customClocks: Array.isArray(w.customClocks) ? w.customClocks : [],
    csfCheck: parseCsfCheck(w.csfCheck),
    assignments: parseAssignments(w.assignments),
  } as Workspace
}

export function toggleCheck(w: Workspace, itemId: string, done: boolean, now = new Date()): Workspace {
  const prev = w.checks[itemId] ?? { done: false }
  // Unticking forgets a "decided not to" outcome so the question can be asked again.
  const { declined: _d, note: _n, ...keep } = prev
  return {
    ...w,
    updatedAt: now.toISOString(),
    checks: { ...w.checks, [itemId]: { ...(done ? prev : keep), done, doneAt: done ? now.toISOString() : undefined } },
  }
}

export function setOwner(w: Workspace, itemId: string, owner: string, now = new Date()): Workspace {
  const prev = w.checks[itemId] ?? { done: false }
  return { ...w, updatedAt: now.toISOString(), checks: { ...w.checks, [itemId]: { ...prev, owner } } }
}

export function addLog(w: Workspace, text: string, now = new Date()): Workspace {
  const t = text.trim()
  if (!t) return w
  return { ...w, updatedAt: now.toISOString(), log: [...w.log, { id: uid(), at: now.toISOString(), text: t }] }
}

export function progress(w: Workspace, itemIds: string[]): { done: number; total: number } {
  const done = itemIds.filter((id) => w.checks[id]?.done).length
  return { done, total: itemIds.length }
}

export function setClockOverride(w: Workspace, obligationId: string, patch: Partial<ClockOverride> | null, now = new Date()): Workspace {
  const next = { ...w.clockOverrides }
  if (patch === null) delete next[obligationId]
  else next[obligationId] = { ...next[obligationId], ...patch }
  return { ...w, updatedAt: now.toISOString(), clockOverrides: next }
}

export function addCustomClock(w: Workspace, label: string, dueIso: string, note = "", now = new Date()): Workspace {
  if (!label.trim() || Number.isNaN(new Date(dueIso).getTime())) return w
  return { ...w, updatedAt: now.toISOString(), customClocks: [...w.customClocks, { id: uid(), label: label.trim(), dueIso, note }] }
}

export function removeCustomClock(w: Workspace, id: string, now = new Date()): Workspace {
  return { ...w, updatedAt: now.toISOString(), customClocks: w.customClocks.filter((c) => c.id !== id) }
}

/** Pause a clock now. No-op if it is already paused. */
export function pauseClock(w: Workspace, obligationId: string, reason = "", now = new Date()): Workspace {
  const pauses = w.clockOverrides[obligationId]?.pauses ?? []
  if (pauses.some((p) => !p.toIso)) return w
  return setClockOverride(w, obligationId, { pauses: [...pauses, { fromIso: now.toISOString(), reason }] }, now)
}

/** Resume a paused clock now. No-op if it isn't paused. */
export function resumeClock(w: Workspace, obligationId: string, now = new Date()): Workspace {
  const pauses = w.clockOverrides[obligationId]?.pauses ?? []
  if (!pauses.some((p) => !p.toIso)) return w
  return setClockOverride(w, obligationId, { pauses: pauses.map((p) => (p.toIso ? p : { ...p, toIso: now.toISOString() })) }, now)
}

const CSF_IDS = ["GV", "ID", "PR", "DE", "RS", "RC"] as const
function parseCsfCheck(raw: unknown): CsfCheck {
  const out: CsfCheck = {}
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    for (const k of CSF_IDS) {
      const v = (raw as Record<string, unknown>)[k]
      if (v === "yes" || v === "partly" || v === "no") out[k] = v
    }
  }
  return out
}

export function setCsfAnswer(w: Workspace, fn: (typeof CSF_IDS)[number], v: "yes" | "partly" | "no", now = new Date()): Workspace {
  return { ...w, updatedAt: now.toISOString(), csfCheck: { ...w.csfCheck, [fn]: v } }
}

function parseAssignments(raw: unknown): Assignment[] {
  if (!Array.isArray(raw)) return []
  return raw.flatMap((a): Assignment[] => {
    if (!a || typeof a !== "object") return []
    const x = a as Partial<Assignment>
    if (typeof x.id !== "string" || typeof x.title !== "string" || !x.title.trim()) return []
    const status = x.status === "done" || x.status === "cancelled" ? x.status : "open"
    return [{
      id: x.id, title: x.title, contactId: typeof x.contactId === "string" ? x.contactId : null,
      assigneeName: typeof x.assigneeName === "string" ? x.assigneeName : "",
      status, createdAt: typeof x.createdAt === "string" ? x.createdAt : new Date().toISOString(),
      dueAt: typeof x.dueAt === "string" ? x.dueAt : undefined, doneAt: typeof x.doneAt === "string" ? x.doneAt : undefined,
      note: typeof x.note === "string" ? x.note : "", itemId: typeof x.itemId === "string" ? x.itemId : undefined,
    }]
  })
}
